import React, {
    FC,
    MutableRefObject,
    ReactElement,
    RefObject,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { Grow } from '@material-ui/core'
import { fromEvent, Subscription } from 'rxjs'
import { first } from 'rxjs/operators'
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import Icon, { IconName } from '../../../../React/Common/Components/Icon'
import { InsightsDashboardLanguage } from '../../../../../i18n'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { LanguageUtils } from '../../../../React/Common/Utils/LanguageUtils'
import { ChartData } from '../../../../NonReact/Types/Types'
import { TransitionPopper } from '../TransitionPopper/TransitionPopper'

export type InsightsStatProps = {
    className?: string
    iconName: IconName
    title: string
    value: number
    asString?: (value: number) => string
    vsValue: number
    isVsPercentage: boolean
    isPositiveVsValueGood?: boolean
    graphData: ChartData[]
    weeklyVs?: {
        thisWeek: number
        lastWeek: number
        prevWeek: number
    }
    index?: number
}

const IMPROVE: string = 'improve'
const WORSEN: string = 'worsen'

export const InsightsStat: FC<InsightsStatProps> = ({
    className,
    iconName,
    title,
    vsValue,
    isVsPercentage,
    isPositiveVsValueGood,
    value,
    asString = (v: number): string => v.toString(),
    weeklyVs,
    graphData,
    index
}: InsightsStatProps): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const [isExpanded, setIsExpanded]: ReactState<boolean> = useState<boolean>(false)

    const ref: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const closerSub: MutableRefObject<Nullable<Subscription>> = useRef<Nullable<Subscription>>()

    const vsValueChange: string = useMemo((): string => {
        if (vsValue > 0) {
            return `idis-stat-${isPositiveVsValueGood ? IMPROVE : WORSEN}`
        }

        if (vsValue < 0) {
            return `idis-stat-${isPositiveVsValueGood ? WORSEN : IMPROVE}`
        }

        return EMPTY_STRING
    }, [vsValue, isPositiveVsValueGood])

    const vsValueIncreaseDecrease: string = useMemo((): string => {
        if (vsValue > 0) {
            return `idis-stat-increase`
        }

        if (vsValue < 0) {
            return `idis-stat-decrease`
        }

        return EMPTY_STRING
    }, [vsValue, isPositiveVsValueGood])

    useEffect((): (() => void) => (): void => closerSub.current?.unsubscribe(), [])

    return (
        <>
            {isExpanded ? <article className='idis-popper-background'></article> : <></>}
            <div
                className={`idis-stat ${isExpanded ? 'idis-stat-expanded' : EMPTY_STRING} ${className ?? EMPTY_STRING}`}
                ref={ref}
                onClick={(): void => {
                    setIsExpanded(true)
                    closerSub.current = fromEvent(document, 'click')
                        .pipe(
                            first(
                                (e: Event): boolean =>
                                    !(e.target as HTMLElement)?.closest(`.${className ?? 'idis-stat'}`)
                            )
                        )
                        .subscribe((): void => setIsExpanded(false))
                }}
            >
                <Icon className='idis-stat-icon' iconName={iconName} />
                <div className='idis-stat-title'>{title}</div>
                <div className='idis-stat-time-period'>
                    {language.get(InsightsDashboardLanguage.InsightsDashboard_OverLast30Days)}
                </div>
                <div className='idis-stat-value'>{asString(value)}</div>
                <TransitionPopper
                    timeout={150}
                    className='idis-popper insights-dashboard'
                    isOpen={isExpanded}
                    anchorEl={ref}
                    style={{
                        minWidth: ref.current?.getBoundingClientRect().width ?? 0,
                        marginTop: -(ref.current?.getBoundingClientRect().height ?? 0)
                    }}
                    placement='bottom'
                    Transition={Grow}
                >
                    <div className='idis-popper-content id-stats'>
                        <div className={`idis-stat ${className ?? EMPTY_STRING}`}>
                            <Icon className='idis-stat-icon' iconName={iconName} />
                            <div className='idis-stat-title'>{title}</div>
                            <div className='idis-stat-time-period'>
                                {language.get(InsightsDashboardLanguage.InsightsDashboard_OverLast30Days)}
                            </div>
                            <div className='idis-stat-value'>{asString(value)}</div>

                            <ResponsiveContainer width='100%' height={120} className='idis-stat-chart'>
                                <AreaChart className='idis-chart idis-chart-times' data={graphData}>
                                    <Tooltip
                                        wrapperClassName='idis-tooltip'
                                        separator={EMPTY_STRING}
                                        labelFormatter={(label: string): string =>
                                            language.getInterpolated(
                                                InsightsDashboardLanguage.InsightsDashboard_Tooltip_Stats_WeekCommencing,
                                                graphData[Number(label) ?? 0]?.name
                                            )
                                        }
                                        formatter={(v: string): [string, string] => [
                                            language.getInterpolated(
                                                `InsightsDashboard_Tooltip_Stats_${index ?? 0}`,
                                                v
                                            ),
                                            EMPTY_STRING
                                        ]}
                                    />

                                    <Area
                                        isAnimationActive={false}
                                        type='natural'
                                        dataKey='value'
                                        fill='currentColor'
                                        stroke='currentColor'
                                        strokeWidth={2}
                                        dot={{ fill: 'currentColor', fillOpacity: 1, stroke: 'none' }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>

                            <div className={`idis-stat-percentage ${vsValueIncreaseDecrease} ${vsValueChange}`}>
                                {isVsPercentage ? (
                                    <span>
                                        {language.get(InsightsDashboardLanguage.InsightsDashboard_VsLastWeekCurrently)}
                                    </span>
                                ) : (
                                    <></>
                                )}
                                <Icon className='idis-stat-percentage-icon' iconName={IconName.ARROW} />
                                <span>
                                    {LanguageUtils.formatLanguageEntry(
                                        language.getInterpolated(
                                            isVsPercentage
                                                ? InsightsDashboardLanguage.InsightsDashboard_PercentVsLastWeek
                                                : InsightsDashboardLanguage.InsightsDashboard_VsLastWeek,
                                            asString(vsValue < 0 ? vsValue * -1 : vsValue)
                                        )
                                    )}
                                </span>
                            </div>
                            <div className='idis-weekly-vs'>
                                <div className='idiswv-this-week idiswv-week'>
                                    <div className='idiswv-week-name'>
                                        {language.get(InsightsDashboardLanguage.InsightsDashboard_ThisWeek)}
                                    </div>
                                    <div className='idiswv-week-data'>{asString(weeklyVs?.thisWeek ?? 0)}</div>
                                </div>
                                <div className='idiswv-last-week idiswv-week'>
                                    <div className='idiswv-week-name'>
                                        {language.get(InsightsDashboardLanguage.InsightsDashboard_LastWeek)}
                                    </div>
                                    <div className='idiswv-week-data'>{asString(weeklyVs?.lastWeek ?? 0)}</div>
                                </div>
                                <div className='idiswv-prev-week idiswv-week'>
                                    <div className='idiswv-week-name'>
                                        {language.get(InsightsDashboardLanguage.InsightsDashboard_PrevWeek)}
                                    </div>
                                    <div className='idiswv-week-data'>{asString(weeklyVs?.prevWeek ?? 0)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TransitionPopper>
            </div>
        </>
    )
}
